/**
 * @fileoverview User row actions.
 * @description Builds a User Management row's three-dot menu from the actions the backend allows
 * the viewer on that user (`allowedActions`, which it works out for the caller): View always;
 * then Edit, Reactivate, Resend invitation, Deactivate and Revoke invitation when listed. Nothing
 * is re-derived from the viewer's privileges or the user's status, so a pending user outside the
 * viewer's downline (whose Revoke would be refused with a 403) never shows Revoke. Resend also
 * needs the invitation id the directory carries.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { DirectoryUser, UserAllowedAction } from '@/features/users/types/users.types'
import type { RowAction } from '@/shared/components/molecules/RowActionsMenu'
import type { TFunction } from 'i18next'

/** What a row's actions do; each is handed the row's user. */
export interface UserRowHandlers {
  onView: (userId: string) => void
  onEdit: (userId: string) => void
  onReactivate: (userId: string) => void
  onDeactivate: (userId: string) => void
  onResendInvitation: (user: DirectoryUser) => void
  onRevokeInvitation: (user: DirectoryUser) => void
}

/**
 * @description Builds a row's menu from the actions the backend allows on the user.
 * @param {TFunction} t - The translate function.
 * @param {DirectoryUser} user - The row's user.
 * @param {UserRowHandlers} handlers - What each action does.
 * @returns {RowAction[]} The menu items, in order.
 */
export function buildUserRowActions(
  t: TFunction,
  user: DirectoryUser,
  handlers: UserRowHandlers,
): RowAction[] {
  /**
   * @description Whether the backend allows the viewer this action on the user.
   * @param {UserAllowedAction} action - The action to check.
   * @returns {boolean} True when `allowedActions` lists it.
   */
  const allows = (action: UserAllowedAction) => user.allowedActions.includes(action)
  const { userId } = user

  const actions: (RowAction | false)[] = [
    {
      key: 'view',
      label: t('sales.userManagement.view'),
      /**
       * @description Opens the row's user in View User.
       */
      onSelect: () => handlers.onView(userId),
    },
    allows('EDIT') && {
      key: 'edit',
      label: t('sales.userManagement.edit'),
      /**
       * @description Opens the row's user in Edit User.
       */
      onSelect: () => handlers.onEdit(userId),
    },
    allows('REACTIVATE') && {
      key: 'reactivate',
      label: t('sales.userManagement.reactivate'),
      /**
       * @description Opens the row's user in Reactivate User.
       */
      onSelect: () => handlers.onReactivate(userId),
    },
    allows('RESEND_INVITATION') &&
      user.pendingInvitationId !== null && {
        key: 'resend-invitation',
        label: t('sales.userManagement.resendInvitation'),
        /**
         * @description Re-sends the row's user's pending invitation.
         */
        onSelect: () => handlers.onResendInvitation(user),
      },
    allows('DEACTIVATE') && {
      key: 'deactivate',
      label: t('sales.userManagement.deactivate'),
      /**
       * @description Opens the row's user in Deactivate User.
       */
      onSelect: () => handlers.onDeactivate(userId),
      tone: 'danger',
    },
    allows('REVOKE_INVITATION') && {
      key: 'revoke-invitation',
      label: t('sales.userManagement.revokeInvitation'),
      /**
       * @description Opens Revoke Invitation for the row's pending invitation.
       */
      onSelect: () => handlers.onRevokeInvitation(user),
      tone: 'danger',
    },
  ]
  return actions.filter((action): action is RowAction => action !== false)
}
